import React, { useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { EventLogType } from 'app/context/EventLogContext';
import EventLog from 'app/apiService/eventLog/eventLogApiService';
import { CircularProgress, Typography, Box } from '@mui/material';
import EventLogCard from './EventLogCard';

const EventLogs = ({ userId }: { userId: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchLogs = async ({ pageParam = 1 }) => {
    const limit = 7;
    const skip = (pageParam - 1) * limit;
    const response = await EventLog.getEventLogs({ user_id: userId, skip, limit });
    return response.body;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useInfiniteQuery({
    queryKey: ['event-logs', userId],
    queryFn: fetchLogs,
    getNextPageParam: last => {
      const { meta } = last;
      return meta.currentPage < meta.totalPages
        ? meta.currentPage + 1
        : undefined;
    },
  });

  const allLogs: EventLogType[] =
    data?.pages.flatMap(page => page.data) || [];

  if (status === 'loading') {
    return (
      <Box
        sx={{
          width: '100%',
          height: 500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fff',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box
        sx={{
          width: '100%',
          height: 500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fff',
        }}
      >
        <Typography color="error">Error loading logs</Typography>
      </Box>
    );
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop <=
        el.clientHeight + 100 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        width: '100%',
        height: 500,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
      }}
    >
      {allLogs.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography>No logs available</Typography>
        </Box>
      ) : (
        allLogs.map(log => (
          <Box
            key={log._id}
            sx={{ flex: '0 0 auto', width: '100%', px: 2, py: 1 }}
          >
            <EventLogCard log={log} />
          </Box>
        ))
      )}

      {isFetchingNextPage && hasNextPage && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            py: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default EventLogs;
